import type { ScoringProfile, ScoringType } from "@/lib/scoring/types";
import {
  FIT_CRITERION_KEYS,
  INTENT_CRITERION_KEYS,
  getCriterionKey,
} from "@/lib/scoring/criterionRegistry";

export type ScoringFilterType = "fit" | "intent";

export type ScoringFilterStatus = {
  enabled: boolean;
  disabledReason: string | null;
};

/**
 * Pure function that determines whether a scoring filter (fit or intent)
 * should be enabled based on active profiles and their criteria.
 */
export function getScoringFilterStatus(
  type: ScoringFilterType,
  activeProfileIds: number[],
  profiles: ScoringProfile[]
): ScoringFilterStatus {
  if (activeProfileIds.length === 0) {
    return {
      enabled: false,
      disabledReason: "Apply a scoring profile to use this filter.",
    };
  }

  if (activeProfileIds.length > 1) {
    return {
      enabled: false,
      disabledReason:
        "Scoring filters are disabled when multiple scoring profiles are active.",
    };
  }

  // Exactly one active profile — check if it has criteria of the requested type
  const activeProfile = profiles.find((p) => p.id === activeProfileIds[0]);
  if (!activeProfile) {
    return {
      enabled: false,
      disabledReason: "Apply a scoring profile to use this filter.",
    };
  }

  const { criteria } = activeProfile.parameters;
  const keys = type === "fit" ? FIT_CRITERION_KEYS : INTENT_CRITERION_KEYS;

  const hasCriteria = criteria.some((c: ScoringType) => {
    const key = getCriterionKey(c);
    return key !== null && (keys as string[]).includes(key);
  });

  if (!hasCriteria) {
    return {
      enabled: false,
      disabledReason:
        type === "fit"
          ? "None of the active scoring profiles include fit criteria."
          : "None of the active scoring profiles include intent criteria.",
    };
  }

  return { enabled: true, disabledReason: null };
}
